import React from 'react'
import { graphql, useStaticQuery } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import { IN_CA, IN_UK, IN_AU, IS_FOREIGN_COUNTRY } from 'src/utils/constants'
import {
  Section,
  ManifestoSection,
  GridContainer,
  ImageContainer,
  LogoContainer,
  Text,
  TwoColumnGrid,
  Column,
  TextContainer,
  ManifestoHeader,
  ManifestoText,
  Title,
} from './styles'

const CalendarPress = () => {
  const {
    wallStreet,
    apartment,
    parents,
    today,
    breakfast,
    morningHerald,
    manifesto,
    mobileManifesto,
    manifestoUSA,
    dailyStar,
  } = useStaticQuery(graphql`
    query CalendarPressQuery {
      wallStreet: file(relativePath: { eq: "calendar/wallstreet.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      apartment: file(relativePath: { eq: "calendar/apartment-therapy.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      parents: file(relativePath: { eq: "calendar/parents.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      today: file(relativePath: { eq: "calendar/today.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      breakfast: file(relativePath: { eq: "calendar/breakfast.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      morningHerald: file(relativePath: { eq: "calendar/morning-herald.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      manifesto: file(relativePath: { eq: "calendar/manifesto.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      mobileManifesto: file(relativePath: { eq: "calendar/mobile-manifesto.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      manifestoUSA: file(relativePath: { eq: "calendar/manifesto-usa.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      dailyStar: file(relativePath: { eq: "calendar/dailyStar.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
    }
  `)
  const pressDetails = [
    {
      image: wallStreet,
      quote: `“The calendar with the cult-like following!”`,
      alt: `Wall Street`,
      id: 'wallStreet',
      href: 'https://www.wsj.com/tech/personal-tech/your-schedules-just-got-crazy-again-try-these-tech-tools-6153a3f?st=a9cwh7iah46sg0z&reflink=desktopwebshare_permalink',
    },
    {
      image: parents,
      quote: `“A must-have for busy families.”`,
      alt: `Parents`,
      id: 'parents',
      href: 'https://www.parents.com/planners-calendars-2025-8764714',
    },
    {
      image: apartment,
      quote: `“Makes life seem significantly easier to manage!”`,
      alt: `Apartment Therapy`,
      id: 'apartment',
      href: 'https://www.apartmenttherapy.com/skylight-digital-calendar-review-37024764',
    },
    ...(IN_CA
      ? [
          {
            image: breakfast,
            quote: `“I've been in the tech world for a long time, this is one of my favorite finds!”`,
            alt: `Breakfast Television`,
            id: 'breakfast',
            href: 'https://www.breakfasttelevision.ca/videos/this-smart-calendar-is-truly-the-secret-to-juggling-it-all/',
          },
        ]
      : IN_UK
      ? [
          {
            image: dailyStar,
            quote: `“I have found the best parenting device, and it actually masquerades as a calendar”`,
            alt: `Daily Star`,
            id: 'dailyStar',
            href: 'https://www.dailystar.co.uk/news/latest-news/i-found-best-parenting-device-35051569',
          },
        ]
      : IN_AU
      ? [
          {
            image: morningHerald,
            quote: `“Built-in features include chore charts, meal planning and custom lists for task tracking and groceries. Just don't complain when you have to do your chores.”`,
            alt: `Morning Herald`,
            id: 'herald',
            href: 'https://www.smh.com.au/lifestyle/life-and-relationships/from-the-first-time-mum-to-the-fitness-lover-here-are-the-best-mother-s-day-gifts-20240422-p5flmu.html',
          },
        ]
      : [
          {
            image: today,
            quote: `“All you need for your family- it's a calendar, it's chores… autonomy for kids.”`,
            alt: `Today Show`,
            id: 'today',
            href: 'https://www.today.com/video/shop-these-back-to-school-essentials-for-your-kids-190634565882',
          },
        ]),
  ]
  return (
    <>
      <Section>
        <Title>Skylight Calendar in the spotlight</Title>
        <GridContainer>
          {pressDetails.map((press) => (
            <div key={press.id}>
              <a href={press.href} target="_blank" rel="noopener noreferrer">
                <LogoContainer id={press.id}>
                  <GatsbyImage
                    image={press.image.childImageSharp.gatsbyImageData}
                    alt={press.alt}
                  />
                </LogoContainer>
              </a>
              <Text>{press.quote}</Text>
            </div>
          ))}
        </GridContainer>
      </Section>
      <ManifestoSection>
        <TwoColumnGrid>
          <Column>
            <TextContainer>
              <ManifestoHeader>
                At the <span>heart</span> of it
              </ManifestoHeader>
              <ManifestoText>
                We are dedicated to bringing harmony and joy to every family by removing the pain
                points and friction that break a family down. We're on a mission to liberate
                families from outdated scheduling methods while easing the mental burden of every
                caregiver, one Skylight Calendar at a time.
              </ManifestoText>
            </TextContainer>
          </Column>
          <Column>
            <ImageContainer id="manifesto">
              <GatsbyImage
                image={
                  !IS_FOREIGN_COUNTRY
                    ? manifestoUSA.childImageSharp.gatsbyImageData
                    : manifesto.childImageSharp.gatsbyImageData
                }
                alt="Family in the kitchen with a skylight calendar"
              />
              <GatsbyImage
                image={
                  !IS_FOREIGN_COUNTRY
                    ? manifestoUSA.childImageSharp.gatsbyImageData
                    : mobileManifesto.childImageSharp.gatsbyImageData
                }
                alt="Family in the kitchen with a skylight calendar"
              />
            </ImageContainer>
          </Column>
        </TwoColumnGrid>
      </ManifestoSection>
    </>
  )
}

export default CalendarPress
